import { useEffect, useMemo, useState } from "react";
import {
    FaBookOpen, FaCalendarAlt, FaCheckCircle, FaDownload, FaEye,
    FaFileAlt, FaInfoCircle, FaListUl, FaSearch, FaSlidersH, FaTasks, FaTimes,
} from "react-icons/fa";
import { COURSES_CONSTANTS } from "../_MODULES/_API";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { ReadOnlyInfo } from "../../../../../_CORE/APP/Templates/_UI/ReadOnly/_index";
import { Badge, EmptyState, Spinner } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ManualMarkdownEditor } from "../../_Manuals/_UI/ManualMarkdownEditor";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { downloadCourseMarkdown, downloadCoursePdf } from "./courseExport.helpers";
import "./_Styles/Courses.css";

const COURSE_STATUS = {
    IDEA: "Idea",
    DRAFT: "Borrador",
    PLANNING: "Planificando",
    READY: "Preparado",
    ACTIVE: "En curso",
    PAUSED: "Pausado",
    COMPLETED: "Finalizado",
};

const COURSE_LEVEL = {
    BEGINNER: "Inicial",
    BASIC: "Basico",
    INTERMEDIATE: "Intermedio",
    ADVANCED: "Avanzado",
    ALL_LEVELS: "Mixto",
};

const COURSE_MODALITY = {
    IN_PERSON: "Presencial",
    LIVE_ONLINE: "Online en vivo",
    ONLINE: "Online",
    HYBRID: "Hibrido",
    SELF_PACED: "Material asincronico",
    WORKSHOP: "Taller / workshop",
    BOOTCAMP: "Intensivo / bootcamp",
};

const NOTE_TYPE = {
    TOPIC: "Tema",
    NOTE: "Apunte",
    SUMMARY: "Resumen",
    EXAMPLE: "Ejemplo",
    EXERCISE: "Ejercicio",
    QUESTION: "Pregunta",
    RESOURCE: "Recurso",
    IDEA: "Idea",
    CHECKLIST: "Checklist",
    REFERENCE: "Referencia",
    OTHER: "Otro",
    CLASS: "Tema",
    VIDEO: "Recurso",
    LIVE_CLASS: "Clase",
    PRACTICE: "Practica",
    PROJECT: "Proyecto",
    READING: "Lectura",
    QUIZ: "Pregunta",
    EXAM: "Evaluacion",
    DEMO: "Ejemplo",
    LAB: "Practica",
};

const NOTE_STATUS = {
    TO_PREPARE: "Por preparar",
    DRAFT: "Borrador",
    READY: "Listo",
    TAUGHT: "Ya ensenado",
    REVIEW: "Revisar",
    PLANNED: "Por preparar",
    IN_PROGRESS: "En preparacion",
    RECORDED: "Listo",
    PUBLISHED: "Listo",
    COMPLETED: "Ya ensenado",
    ON_HOLD: "Revisar",
};

const TASK_TYPE = {
    PREPARE_CLASS: "Preparar clase",
    REVIEW_TOPIC: "Revisar tema",
    CREATE_EXAMPLE: "Crear ejemplo",
    CREATE_EXERCISE: "Crear ejercicio",
    RESEARCH: "Investigar",
    MATERIAL: "Material",
    FOLLOW_UP: "Seguimiento",
    ADMIN: "Administrativo",
    OTHER: "Otro",
    PREPARATION: "Preparar clase",
    CONTENT: "Contenido",
    RECORDING: "Material",
    EDITING: "Revision",
    REVIEW: "Revisar tema",
    PUBLISHING: "Material",
    STUDENT_SUPPORT: "Seguimiento",
    MARKETING: "Otro",
};

const TASK_STATUS = {
    TODO: "Pendiente",
    IN_PROGRESS: "En progreso",
    BLOCKED: "Bloqueada",
    DONE: "Hecha",
    CANCELLED: "Cancelada",
};

const TASK_PRIORITY = {
    LOW: "Baja",
    MEDIUM: "Media",
    HIGH: "Alta",
    URGENT: "Urgente",
};

const SCHEDULE_TYPE = {
    CLASS: "Clase",
    STUDY: "Estudio / repaso",
    PREPARATION: "Preparacion",
    REVIEW: "Revision",
    DEADLINE: "Fecha limite",
    MEETING: "Reunion",
    OTHER: "Otro",
    LIVE_CLASS: "Clase",
    RECORDING: "Preparacion",
    OFFICE_HOURS: "Consultas",
    EXAM: "Evaluacion",
    RELEASE: "Entrega / publicacion",
};

const SCHEDULE_STATUS = {
    PLANNED: "Planificado",
    CONFIRMED: "Confirmado",
    DONE: "Realizado",
    MOVED: "Reprogramado",
    CANCELLED: "Cancelado",
};

const safeText = (value, fallback = "Sin definir.") => {
    const text = String(value ?? "").trim();
    return text || fallback;
};

const formatDate = (value) => value
    ? new Date(value).toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" })
    : "Sin fecha";

const formatDateTime = (value) => value
    ? new Date(value).toLocaleString("es-AR", {
        day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    })
    : "Sin fecha";

const hasFullCourse = (course) => !!course
    && Array.isArray(course.modules)
    && Array.isArray(course.tasks)
    && Array.isArray(course.schedule);

const defaultSelection = () => ({ kind: "overview", id: null });


const MarkdownBlock = ({ value, empty = "Sin contenido." }) => {
    const source = String(value || "").trim();
    if (!source) return <div className="LStudio__course_reader_prose">{empty}</div>;
    return <div className="LStudio__course_reader_markdown">
        <ManualMarkdownEditor value={source} readOnly minHeight={360} ariaLabel="Contenido Markdown" />
    </div>;
};

const SectionText = ({ title, value, empty = "Sin contenido." }) => (
    <section className="LStudio__course_reader_section">
        <h2>{title}</h2>
        <div className="LStudio__course_reader_prose">{safeText(value, empty)}</div>
    </section>
);

export const CourseReadModal = ({
    isOpen,
    onClose,
    course = null,
    courseId = null,
    initialSelection = null,
}) => {
    const targetId = courseId || course?.id || null;
    const initialKind = initialSelection?.kind || null;
    const initialId = initialSelection?.id || null;
    const [localCourse, setLocalCourse] = useState(course);
    const [selection, setSelection] = useState(initialSelection || { kind: "overview", id: null });
    const [search, setSearch] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ module: "ALL", type: "ALL", status: "ALL" });

    const { apiGet, useApiData, useApiLoading } = useApiQuery({
        minTimeQuery: 120,
        enablePagination: false,
        startLoading: false,
    });

    useEffect(() => {
        if (!isOpen) return;
        setLocalCourse(course || null);
        setSearch("");
        setAdvancedOpen(false);
        setAdvanced({ module: "ALL", type: "ALL", status: "ALL" });
        setSelection(initialKind ? { kind: initialKind, id: initialId } : defaultSelection());
    }, [isOpen, course?.id, initialKind, initialId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen || !targetId || hasFullCourse(course)) return;
        apiGet({
            endpoint: COURSES_CONSTANTS.DETAIL,
            isDetail: true,
            config: { pathParams: { id: targetId } },
        });
    }, [isOpen, targetId, course]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (useApiData?.id && useApiData.id === targetId) setLocalCourse(useApiData);
    }, [useApiData, targetId]);

    const currentCourse = useApiData?.id === targetId ? useApiData : localCourse;
    const modules = currentCourse?.modules || [];
    const tasks = currentCourse?.tasks || [];
    const schedule = currentCourse?.schedule || [];
    const notes = useMemo(
        () => modules.flatMap((module) => (module.lessons || []).map((item) => ({
            ...item,
            moduleId: item.moduleId || module.id,
            moduleTitle: module.title,
        }))),
        [modules],
    );

    useEffect(() => {
        if (!isOpen || !currentCourse) return;
        if (initialKind) {
            setSelection({ kind: initialKind, id: initialId });
            return;
        }
        setSelection((current) => current?.kind ? current : defaultSelection());
    }, [isOpen, currentCourse?.id, initialKind, initialId]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedModule = selection.kind === "module"
        ? modules.find((item) => item.id === selection.id)
        : selection.kind === "lesson"
            ? modules.find((module) => (module.lessons || []).some((item) => item.id === selection.id))
            : null;
    const selectedNote = selection.kind === "lesson" ? notes.find((item) => item.id === selection.id) : null;
    const selectedTask = selection.kind === "task" ? tasks.find((item) => item.id === selection.id) : null;
    const selectedEvent = selection.kind === "schedule" ? schedule.find((item) => item.id === selection.id) : null;
    const selectedRecord = selectedNote || selectedModule || selectedTask || selectedEvent || currentCourse;

    const query = search.trim().toLowerCase();
    const advancedCount = [advanced.module !== "ALL", advanced.type !== "ALL", advanced.status !== "ALL"].filter(Boolean).length;
    const moduleOptions = useMemo(() => [
        { value: "ALL", label: "Todos los módulos" },
        ...modules.map((module) => ({ value: String(module.id), label: module.title || "Módulo sin título" })),
    ], [modules]);
    const noteTypeOptions = useMemo(() => {
        const values = [...new Set(notes.map((item) => item.type).filter(Boolean))];
        return [{ value: "ALL", label: "Todos los tipos" }, ...values.map((value) => ({ value, label: NOTE_TYPE[value] || value }))];
    }, [notes]);
    const noteStatusOptions = useMemo(() => {
        const values = [...new Set(notes.map((item) => item.status).filter(Boolean))];
        return [{ value: "ALL", label: "Todos los estados" }, ...values.map((value) => ({ value, label: NOTE_STATUS[value] || value }))];
    }, [notes]);
    const visibleModules = useMemo(() => modules.reduce((result, module) => {
        if (advanced.module !== "ALL" && String(module.id) !== String(advanced.module)) return result;

        const advancedLessons = (module.lessons || []).filter((item) => {
            if (advanced.type !== "ALL" && String(item.type || "") !== String(advanced.type)) return false;
            if (advanced.status !== "ALL" && String(item.status || "") !== String(advanced.status)) return false;
            return true;
        });
        const hasNoteFilter = advanced.type !== "ALL" || advanced.status !== "ALL";
        if (hasNoteFilter && advancedLessons.length === 0) return result;

        if (!query) {
            result.push({ ...module, lessons: advancedLessons });
            return result;
        }

        const moduleMatch = [module.title, module.description, module.objectives]
            .filter(Boolean).join(" ").toLowerCase().includes(query);
        const lessons = advancedLessons.filter((item) => [
            item.title, item.description, item.content, item.instructorNotes, NOTE_TYPE[item.type], NOTE_STATUS[item.status],
        ].filter(Boolean).join(" ").toLowerCase().includes(query));
        if (moduleMatch || lessons.length) result.push({ ...module, lessons: moduleMatch ? advancedLessons : lessons });
        return result;
    }, []), [modules, query, advanced]);
    const visibleNoteCount = useMemo(() => visibleModules.reduce((sum, module) => sum + (module.lessons || []).length, 0), [visibleModules]);

    const selectedTitle = selectedNote?.title
        || selectedModule?.title
        || selectedTask?.title
        || selectedEvent?.title
        || (selection.kind === "tasks" ? "Tareas" : null)
        || (selection.kind === "schedule-list" ? "Itinerario" : null)
        || (selection.kind === "info" ? "Ficha del curso" : null)
        || currentCourse?.title
        || "Curso";

    const renderOverview = () => (
        <div className="LStudio__course_reader_article">
            <div className="LStudio__course_reader_article_head">
                <span className="LStudio__course_reader_article_icon"><FaBookOpen /></span>
                <div>
                    <p className="LStudio__course_reader_kicker">Cuaderno docente</p>
                    <h1>{currentCourse.title}</h1>
                    <p>{safeText(currentCourse.description, "Sin descripcion general.")}</p>
                </div>
            </div>

            <div className="LStudio__course_reader_stats">
                <span><strong>{modules.length}</strong> modulos</span>
                <span><strong>{notes.length}</strong> apuntes</span>
                <span><strong>{tasks.length}</strong> tareas</span>
                <span><strong>{schedule.length}</strong> eventos</span>
            </div>

            <SectionText title="A quien se lo voy a ensenar" value={currentCourse.audience} />
            <SectionText title="Que quiero que aprendan" value={currentCourse.objectives} />
            <SectionText title="Que deberian saber antes" value={currentCourse.prerequisites} />
        </div>
    );

    const renderModule = () => {
        if (!selectedModule) return renderOverview();
        const moduleNotes = selectedModule.lessons || [];
        return (
            <div className="LStudio__course_reader_article">
                <div className="LStudio__course_reader_article_head">
                    <span className="LStudio__course_reader_article_icon"><FaListUl /></span>
                    <div>
                        <p className="LStudio__course_reader_kicker">Modulo</p>
                        <h1>{selectedModule.title}</h1>
                        <p>{safeText(selectedModule.description, "Sin descripcion para este modulo.")}</p>
                    </div>
                </div>
                <SectionText title="Que quiero cubrir" value={selectedModule.objectives} />
                <section className="LStudio__course_reader_section">
                    <h2>Apuntes de este modulo</h2>
                    {moduleNotes.length ? (
                        <div className="LStudio__course_reader_link_list">
                            {moduleNotes.map((item) => (
                                <button key={item.id} type="button" onClick={() => setSelection({ kind: "lesson", id: item.id })}>
                                    <FaFileAlt />
                                    <span>
                                        <strong>{item.title}</strong>
                                        <small>{NOTE_TYPE[item.type] || item.type} · {NOTE_STATUS[item.status] || item.status}</small>
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : <p className="LStudio__course_reader_empty_text">Este modulo todavia no tiene apuntes.</p>}
                </section>
            </div>
        );
    };

    const renderNote = () => {
        if (!selectedNote) return renderOverview();
        return (
            <div className="LStudio__course_reader_article">
                <div className="LStudio__course_reader_article_head">
                    <span className="LStudio__course_reader_article_icon"><FaFileAlt /></span>
                    <div>
                        <p className="LStudio__course_reader_kicker">{NOTE_TYPE[selectedNote.type] || "Apunte"} · {selectedNote.moduleTitle}</p>
                        <h1>{selectedNote.title}</h1>
                        <p>{safeText(selectedNote.description, "Sin resumen breve.")}</p>
                    </div>
                </div>
                <section className="LStudio__course_reader_section">
                    <h2>Contenido para ensenar</h2>
                    <MarkdownBlock value={selectedNote.content} />
                </section>
                <SectionText title="Recordatorios privados" value={selectedNote.instructorNotes} empty="Sin recordatorios privados." />
                {selectedNote.resourceUrl && (
                    <section className="LStudio__course_reader_section">
                        <h2>Referencia</h2>
                        <a className="LStudio__course_reader_resource" href={selectedNote.resourceUrl} target="_blank" rel="noreferrer noopener">
                            {selectedNote.resourceUrl}
                        </a>
                    </section>
                )}
            </div>
        );
    };

    const renderTasks = () => (
        <div className="LStudio__course_reader_article">
            <div className="LStudio__course_reader_article_head">
                <span className="LStudio__course_reader_article_icon"><FaTasks /></span>
                <div>
                    <p className="LStudio__course_reader_kicker">Organizacion personal</p>
                    <h1>Tareas</h1>
                    <p>Todo lo que anotaste para preparar, revisar, investigar o recordar.</p>
                </div>
            </div>
            {tasks.length ? (
                <div className="LStudio__course_reader_cards">
                    {tasks.map((task) => (
                        <button key={task.id} type="button" onClick={() => setSelection({ kind: "task", id: task.id })}>
                            <span className="LStudio__course_reader_card_icon"><FaCheckCircle /></span>
                            <span>
                                <strong>{task.title}</strong>
                                <small>{TASK_TYPE[task.type] || task.type} · {TASK_STATUS[task.status] || task.status}{task.dueDate ? ` · ${formatDate(task.dueDate)}` : ""}</small>
                                {task.description && <p>{task.description}</p>}
                            </span>
                        </button>
                    ))}
                </div>
            ) : <EmptyState icon={<FaTasks />} title="No hay tareas" description="Todavia no anotaste tareas para este curso." />}
        </div>
    );

    const renderTask = () => {
        if (!selectedTask) return renderTasks();
        const module = modules.find((item) => item.id === selectedTask.moduleId);
        return (
            <div className="LStudio__course_reader_article">
                <div className="LStudio__course_reader_article_head">
                    <span className="LStudio__course_reader_article_icon"><FaTasks /></span>
                    <div>
                        <p className="LStudio__course_reader_kicker">Tarea personal</p>
                        <h1>{selectedTask.title}</h1>
                        <p>{TASK_TYPE[selectedTask.type] || selectedTask.type} · {TASK_PRIORITY[selectedTask.priority] || selectedTask.priority}</p>
                    </div>
                </div>
                <SectionText title="Descripcion" value={selectedTask.description} />
                <SectionText title="Notas" value={selectedTask.notes} empty="Sin notas adicionales." />
                <div className="LStudio__course_reader_fact_grid">
                    <div><span>Estado</span><strong>{TASK_STATUS[selectedTask.status] || selectedTask.status}</strong></div>
                    <div><span>Modulo</span><strong>{module?.title || "Sin modulo"}</strong></div>
                    <div><span>Vencimiento</span><strong>{selectedTask.dueDate ? formatDate(selectedTask.dueDate) : "Sin fecha"}</strong></div>
                    <div><span>Tiempo estimado</span><strong>{Number(selectedTask.estimatedMinutes || 0)} min</strong></div>
                </div>
            </div>
        );
    };

    const renderSchedule = () => (
        <div className="LStudio__course_reader_article">
            <div className="LStudio__course_reader_article_head">
                <span className="LStudio__course_reader_article_icon"><FaCalendarAlt /></span>
                <div>
                    <p className="LStudio__course_reader_kicker">Planificacion personal</p>
                    <h1>Itinerario</h1>
                    <p>Fechas de clase, preparacion, estudio, revision y cualquier hito que quieras recordar.</p>
                </div>
            </div>
            {schedule.length ? (
                <div className="LStudio__course_reader_timeline">
                    {schedule.map((event) => (
                        <button key={event.id} type="button" onClick={() => setSelection({ kind: "schedule", id: event.id })}>
                            <span className="LStudio__course_reader_timeline_date">{formatDateTime(event.scheduledAt)}</span>
                            <span>
                                <strong>{event.title}</strong>
                                <small>{SCHEDULE_TYPE[event.type] || event.type}{event.location ? ` · ${event.location}` : ""}</small>
                            </span>
                        </button>
                    ))}
                </div>
            ) : <EmptyState icon={<FaCalendarAlt />} title="No hay itinerario" description="Todavia no anotaste fechas para este curso." />}
        </div>
    );

    const renderEvent = () => {
        if (!selectedEvent) return renderSchedule();
        const module = modules.find((item) => item.id === selectedEvent.moduleId);
        return (
            <div className="LStudio__course_reader_article">
                <div className="LStudio__course_reader_article_head">
                    <span className="LStudio__course_reader_article_icon"><FaCalendarAlt /></span>
                    <div>
                        <p className="LStudio__course_reader_kicker">{SCHEDULE_TYPE[selectedEvent.type] || "Evento"}</p>
                        <h1>{selectedEvent.title}</h1>
                        <p>{formatDateTime(selectedEvent.scheduledAt)}</p>
                    </div>
                </div>
                <SectionText title="Descripcion" value={selectedEvent.description} />
                <SectionText title="Notas" value={selectedEvent.notes} empty="Sin notas adicionales." />
                <div className="LStudio__course_reader_fact_grid">
                    <div><span>Estado</span><strong>{SCHEDULE_STATUS[selectedEvent.status] || selectedEvent.status}</strong></div>
                    <div><span>Modulo</span><strong>{module?.title || "Sin modulo"}</strong></div>
                    <div><span>Duracion</span><strong>{Number(selectedEvent.durationMinutes || 0)} min</strong></div>
                    <div><span>Lugar</span><strong>{selectedEvent.location || "Sin definir"}</strong></div>
                </div>
                {selectedEvent.meetingUrl && (
                    <section className="LStudio__course_reader_section">
                        <h2>Enlace</h2>
                        <a className="LStudio__course_reader_resource" href={selectedEvent.meetingUrl} target="_blank" rel="noreferrer noopener">
                            {selectedEvent.meetingUrl}
                        </a>
                    </section>
                )}
            </div>
        );
    };

    const renderInfo = () => (
        <div className="LStudio__course_reader_article">
            <div className="LStudio__course_reader_article_head">
                <span className="LStudio__course_reader_article_icon"><FaInfoCircle /></span>
                <div>
                    <p className="LStudio__course_reader_kicker">Ficha privada</p>
                    <h1>{currentCourse.title}</h1>
                    <p>{safeText(currentCourse.description, "Sin descripcion general.")}</p>
                </div>
            </div>
            <div className="LStudio__course_reader_fact_grid LStudio__course_reader_fact_grid--wide">
                <div><span>Estado</span><strong>{COURSE_STATUS[currentCourse.status] || currentCourse.status || "-"}</strong></div>
                <div><span>Nivel</span><strong>{COURSE_LEVEL[currentCourse.level] || currentCourse.level || "-"}</strong></div>
                <div><span>Contexto</span><strong>{COURSE_MODALITY[currentCourse.modality] || currentCourse.modality || "-"}</strong></div>
                <div><span>Institucion / plataforma</span><strong>{currentCourse.platform || "Sin definir"}</strong></div>
                <div><span>Inicio</span><strong>{currentCourse.startDate ? formatDate(currentCourse.startDate) : "Sin fecha"}</strong></div>
                <div><span>Fin estimado</span><strong>{currentCourse.endDate ? formatDate(currentCourse.endDate) : "Sin fecha"}</strong></div>
            </div>
            <SectionText title="A quien se lo voy a ensenar" value={currentCourse.audience} />
            <SectionText title="Que quiero que aprendan" value={currentCourse.objectives} />
            <SectionText title="Que deberian saber antes" value={currentCourse.prerequisites} />
        </div>
    );

    const renderMain = () => {
        if (!currentCourse) return <Spinner label="Cargando curso..." />;
        if (selection.kind === "module") return renderModule();
        if (selection.kind === "lesson") return renderNote();
        if (selection.kind === "tasks") return renderTasks();
        if (selection.kind === "task") return renderTask();
        if (selection.kind === "schedule-list") return renderSchedule();
        if (selection.kind === "schedule") return renderEvent();
        if (selection.kind === "info") return renderInfo();
        return renderOverview();
    };

    const metaRows = useMemo(() => {
        if (!currentCourse) return [];
        if (selectedNote) return [
            ["Tipo", NOTE_TYPE[selectedNote.type] || selectedNote.type],
            ["Estado", NOTE_STATUS[selectedNote.status] || selectedNote.status],
            ["Modulo", selectedNote.moduleTitle || selectedModule?.title || "-"],
            ["Tiempo", `${Number(selectedNote.durationMinutes || 0)} min`],
        ];
        if (selectedModule) return [
            ["Estado", selectedModule.status || "-"],
            ["Apuntes", String((selectedModule.lessons || []).length)],
            ["Tiempo", `${Number(selectedModule.estimatedMinutes || 0)} min`],
        ];
        if (selectedTask) return [
            ["Estado", TASK_STATUS[selectedTask.status] || selectedTask.status],
            ["Prioridad", TASK_PRIORITY[selectedTask.priority] || selectedTask.priority],
            ["Vence", selectedTask.dueDate ? formatDate(selectedTask.dueDate) : "Sin fecha"],
        ];
        if (selectedEvent) return [
            ["Estado", SCHEDULE_STATUS[selectedEvent.status] || selectedEvent.status],
            ["Fecha", formatDateTime(selectedEvent.scheduledAt)],
            ["Duracion", `${Number(selectedEvent.durationMinutes || 0)} min`],
        ];
        return [
            ["Estado", COURSE_STATUS[currentCourse.status] || currentCourse.status || "-"],
            ["Nivel", COURSE_LEVEL[currentCourse.level] || currentCourse.level || "-"],
            ["Modulos", String(modules.length)],
            ["Apuntes", String(notes.length)],
        ];
    }, [currentCourse, selectedNote, selectedModule, selectedTask, selectedEvent, modules.length, notes.length]);

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            hasCloseButton={false}
            className="LStudio__course_reader_modal"
        >
            <div className="LStudio__course_reader">
                <header className="LStudio__course_reader_header">
                    <div className="LStudio__course_reader_identity">
                        <span><FaBookOpen /></span>
                        <div>
                            <strong>{currentCourse?.title || course?.title || "Curso"}</strong>
                            <small>Modo lectura</small>
                        </div>
                    </div>
                    <div className="LStudio__course_reader_current">{selectedTitle}</div>
                    <div className="LStudio__course_reader_header_actions">
                        <CardActionMenu
                            title="Descargar curso"
                            ariaLabel="Opciones de descarga del curso"
                            popoverClassName="LStudio__course_reader_action_popover"
                            items={[
                                {
                                    key: "download-pdf",
                                    label: "Descargar PDF completo",
                                    icon: <FaFileAlt />,
                                    isDisabled: !hasFullCourse(currentCourse) || useApiLoading,
                                    onClick: () => downloadCoursePdf(currentCourse),
                                },
                                {
                                    key: "download-markdown",
                                    label: "Descargar Markdown por carpetas (.zip)",
                                    icon: <FaDownload />,
                                    isDisabled: !hasFullCourse(currentCourse) || useApiLoading,
                                    onClick: () => downloadCourseMarkdown(currentCourse),
                                },
                            ]}
                        />
                        <span className="LStudio__course_reader_badge"><FaEye /> Solo lectura</span>
                        <button type="button" className="LStudio__course_reader_close" aria-label="Cerrar lectura" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </header>

                <div className="LStudio__course_reader_body">
                    <aside className="LStudio__course_reader_nav">
                        <div className="LStudio__course_reader_filter">
                            <div className="LStudio__course_reader_search">
                                <FaSearch />
                                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Ej.: autenticación, React o deploy" />
                            </div>
                            <button
                                type="button"
                                className="LStudio__course_reader_filter_toggle"
                                data-open={advancedOpen ? "true" : "false"}
                                onClick={() => setAdvancedOpen((current) => !current)}
                                title={advancedCount ? `Filtros avanzados (${advancedCount} activos)` : "Filtros avanzados"}
                                aria-label={advancedCount ? `Filtros avanzados (${advancedCount} activos)` : "Filtros avanzados"}
                            >
                                <FaSlidersH />
                            </button>
                        </div>
                        <AdvancedFilterPanel
                            isOpen={advancedOpen}
                            activeCount={advancedCount}
                            className="LStudio__course_reader_advanced"
                            onClear={() => setAdvanced({ module: "ALL", type: "ALL", status: "ALL" })}
                        >
                            <FieldSelect id="course_reader_module_filter" label="Módulo" value={advanced.module} onChange={(event) => setAdvanced((current) => ({ ...current, module: event.target.value }))} options={moduleOptions} isSearchable />
                            <FieldSelect id="course_reader_type_filter" label="Tipo de apunte" value={advanced.type} onChange={(event) => setAdvanced((current) => ({ ...current, type: event.target.value }))} options={noteTypeOptions} />
                            <FieldSelect id="course_reader_status_filter" label="Estado" value={advanced.status} onChange={(event) => setAdvanced((current) => ({ ...current, status: event.target.value }))} options={noteStatusOptions} />
                        </AdvancedFilterPanel>

                        <div className="LStudio__course_reader_filter_count">
                            {visibleNoteCount} {visibleNoteCount === 1 ? "apunte" : "apuntes"}
                        </div>

                        <nav>
                            <button type="button" data-active={selection.kind === "overview"} onClick={() => setSelection({ kind: "overview", id: null })}>
                                <FaBookOpen /><span>Resumen del curso</span>
                            </button>

                            <div className="LStudio__course_reader_nav_label">Cuaderno</div>
                            {visibleModules.map((module) => (
                                <div key={module.id} className="LStudio__course_reader_nav_group">
                                    <button type="button" data-active={selection.kind === "module" && selection.id === module.id}
                                        onClick={() => setSelection({ kind: "module", id: module.id })}>
                                        <FaListUl /><span>{module.title}</span>
                                    </button>
                                    {(module.lessons || []).map((item) => (
                                        <button key={item.id} type="button" className="is-child"
                                            data-active={selection.kind === "lesson" && selection.id === item.id}
                                            onClick={() => setSelection({ kind: "lesson", id: item.id })}>
                                            <FaFileAlt /><span>{item.title}</span>
                                        </button>
                                    ))}
                                </div>
                            ))}
                            {(query || advancedCount > 0) && visibleModules.length === 0 && (
                                <span className="LStudio__course_reader_nav_empty">Sin apuntes que coincidan con los filtros.</span>
                            )}

                            <div className="LStudio__course_reader_nav_label">Organizacion</div>
                            <button type="button" data-active={selection.kind === "tasks" || selection.kind === "task"}
                                onClick={() => setSelection({ kind: "tasks", id: null })}>
                                <FaTasks /><span>Tareas</span><small>{tasks.length}</small>
                            </button>
                            <button type="button" data-active={selection.kind === "schedule-list" || selection.kind === "schedule"}
                                onClick={() => setSelection({ kind: "schedule-list", id: null })}>
                                <FaCalendarAlt /><span>Itinerario</span><small>{schedule.length}</small>
                            </button>
                            <button type="button" data-active={selection.kind === "info"}
                                onClick={() => setSelection({ kind: "info", id: null })}>
                                <FaInfoCircle /><span>Ficha del curso</span>
                            </button>
                        </nav>
                    </aside>

                    <main className="LStudio__course_reader_main">
                        {useApiLoading && !hasFullCourse(currentCourse) ? <Spinner label="Cargando curso..." /> : renderMain()}
                    </main>

                    <aside className="LStudio__course_reader_meta">
                        <section>
                            <strong>En esta vista</strong>
                            <h3>{selectedTitle}</h3>
                            <p>Consulta la informacion sin modificar ningun dato.</p>
                        </section>
                        <section>
                            <strong>Datos</strong>
                            <div className="LStudio__course_reader_meta_list">
                                {metaRows.map(([label, value]) => (
                                    <div key={label}><span>{label}</span><b>{value || "-"}</b></div>
                                ))}
                            </div>
                        </section>
                        <section>
                            <ReadOnlyInfo
                                heading="Registro"
                                createdAt={selectedRecord?.createdAt}
                                updatedAt={selectedRecord?.updatedAt}
                            />
                        </section>
                    </aside>
                </div>
            </div>
        </AppModal>
    );
};
